/**
 * GraphQL queries and hooks — the Governance & Analytics dashboard widgets
 * added by issue #1657 (part of epic #1650): Recent Changes, Service
 * Complexity, and Team Health.
 */

import { gql } from '@apollo/client';
import { useQuery, type QueryResult } from '@apollo/client/react';

/**
 * Query for the Recent Changes widget (issue #1657). `days` accepts 1-90,
 * defaulting to 7 server-side. Reuses the same Administrator/non-admin
 * audit-query scoping `resolvers/audit.ts`'s `auditEvents` already enforces.
 */
export const GET_RECENT_CHANGES = gql`
  query GetRecentChanges($days: Int) {
    recentChanges(days: $days) {
      changes {
        eventId
        timestamp
        actor
        actorName
        action
        entityType
        entityId
        summary
      }
      total
    }
  }
`;

export interface RecentChangeEntry {
  eventId: string;
  timestamp: string;
  actor: string;
  actorName: string;
  action: string;
  entityType: string;
  entityId: string;
  summary: string;
}

export interface RecentChangesData {
  recentChanges: {
    changes: RecentChangeEntry[];
    total: number;
  };
}

export interface RecentChangesVariables {
  days?: number;
}

/** Hook for the Recent Changes widget's audit-trail activity feed. */
export function useRecentChanges(
  days?: number
): QueryResult<RecentChangesData, RecentChangesVariables> {
  return useQuery(GET_RECENT_CHANGES, {
    variables: { days },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Query for the Service Complexity widget (issue #1657). `limit` defaults
 * to 10 server-side.
 */
export const GET_SERVICE_COMPLEXITY = gql`
  query GetServiceComplexity($limit: Int) {
    serviceComplexity(limit: $limit) {
      services {
        entityId
        name
        complexityScore
        inboundDeps
        outboundDeps
        techCount
        ageMonths
        changeFrequency
        recommendation
      }
    }
  }
`;

export interface ServiceComplexityEntry {
  entityId: string;
  name: string;
  complexityScore: number;
  inboundDeps: number;
  outboundDeps: number;
  techCount: number;
  ageMonths: number;
  changeFrequency: number;
  recommendation: string;
}

export interface ServiceComplexityData {
  serviceComplexity: {
    services: ServiceComplexityEntry[];
  };
}

export interface ServiceComplexityVariables {
  limit?: number;
}

/** Hook for the Service Complexity widget's ranked service list. */
export function useServiceComplexity(
  limit?: number
): QueryResult<ServiceComplexityData, ServiceComplexityVariables> {
  return useQuery(GET_SERVICE_COMPLEXITY, {
    variables: { limit },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}

/**
 * Query for the Team Health widget (issue #1657). `teamId` defaults to the
 * caller's own team server-side when omitted.
 */
export const GET_TEAM_HEALTH = gql`
  query GetTeamHealth($teamId: String) {
    teamHealth(teamId: $teamId) {
      team {
        id
        name
        division
      }
      metrics {
        serviceCount
        productionServices
        developmentServices
        avgComplexity
        deprecatedServices
        techDebtScore
        offStrategyTechCount
      }
      recommendations
    }
  }
`;

export interface TeamHealthTeamInfo {
  id: string;
  name: string;
  division: string;
}

export interface TeamHealthMetrics {
  serviceCount: number;
  productionServices: number;
  developmentServices: number;
  avgComplexity: number;
  deprecatedServices: number;
  techDebtScore: number;
  offStrategyTechCount: number;
}

export interface TeamHealthData {
  teamHealth: {
    team: TeamHealthTeamInfo | null;
    metrics: TeamHealthMetrics | null;
    recommendations: string[];
  };
}

export interface TeamHealthVariables {
  teamId?: string;
}

/** Hook for the Team Health widget's per-team scorecard. */
export function useTeamHealth(teamId?: string): QueryResult<TeamHealthData, TeamHealthVariables> {
  return useQuery(GET_TEAM_HEALTH, {
    variables: { teamId },
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });
}
